// Rótulos das telas internas "Meta de Frentista (interno)". Funções (não constantes) para o
// idioma trocar em runtime; cada `__t('literal')` é visto pelo extrator do catálogo es.
import { goalLevelLabel } from '../../components/AttendantGoalOfferCard/offerCopy';
import { __t } from '../../i18n/t';
import { AttendantGoalType } from '../../models/attendantGoalType';
import {
  GoalTrigger, InsightKind, InsightStatus, OfferEndedReason, OfferLevelOption, OfferRollout, OfferStatus, OfferTargetType,
  SendOfferError, SendOfferErrorCode, SendOfferOutcome, SendOfferResult, StationExclusion, WorkerRunStatus,
} from '../../models/attendantGoalOffer';

type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'warning' | 'success';

export const endedReasonLabel = (reason: OfferEndedReason): string => {
  switch (reason) {
    case OfferEndedReason.expired: return __t('prazo vencido');
    case OfferEndedReason.converted: return __t('virou contrato');
    default: return String(reason);
  }
};

export const statusLabel = (status: OfferStatus, endedReason?: OfferEndedReason | null): string => {
  switch (status) {
    case OfferStatus.pending: return __t('Aguardando resposta');
    case OfferStatus.dismissed: return __t('Agora não');
    case OfferStatus.superseded: return __t('Fechado sozinho');
    case OfferStatus.active: return __t('Em degustação');
    case OfferStatus.ended: return endedReason ? `${__t('Encerrado')} · ${endedReasonLabel(endedReason)}` : __t('Encerrado');
    default: return String(status);
  }
};

export const statusColor = (status: OfferStatus): ChipColor => {
  switch (status) {
    case OfferStatus.pending: return 'info';
    case OfferStatus.active: return 'success';
    case OfferStatus.dismissed: return 'warning';
    default: return 'default';
  }
};

// Ordem do funil: criado, "agora não", em degustação, encerrado, fechado sozinho.
const STATUS_ORDER = [OfferStatus.pending, OfferStatus.dismissed, OfferStatus.active, OfferStatus.ended, OfferStatus.superseded];
export const statusChoices = () => STATUS_ORDER.map(id => ({ id, name: statusLabel(id) }));

export const rolloutLabel = (rollout: OfferRollout): string => {
  switch (rollout) {
    case OfferRollout.preview: return __t('Prévia');
    case OfferRollout.pilot: return __t('Piloto');
    case OfferRollout.standard: return __t('Geral');
    case OfferRollout.manual: return __t('Manual');
    default: return String(rollout);
  }
};

export const rolloutColor = (rollout: OfferRollout): ChipColor => {
  switch (rollout) {
    case OfferRollout.preview: return 'secondary';
    case OfferRollout.pilot: return 'primary';
    case OfferRollout.manual: return 'info';
    default: return 'default';
  }
};

export const rolloutChoices = () => Object.values(OfferRollout).map(id => ({ id, name: rolloutLabel(id) }));

export const insightKindLabel = (kind: InsightKind): string => {
  switch (kind) {
    case InsightKind.offerPitch: return __t('Texto do convite');
    case InsightKind.trialWeekly: return __t('Leitura semanal');
    case InsightKind.trialFinal: return __t('Leitura final');
    default: return String(kind);
  }
};

export const insightStatusLabel = (status: InsightStatus): string => {
  switch (status) {
    case InsightStatus.ready: return __t('Pronta');
    case InsightStatus.generating: return __t('Gerando');
    case InsightStatus.unavailable: return __t('Indisponível');
    case InsightStatus.insufficientData: return __t('Dados insuficientes');
    default: return String(status);
  }
};

export const triggerLabel = (trigger: GoalTrigger): string => {
  switch (trigger) {
    case GoalTrigger.lowTail: return __t('T1 · produtividade abaixo da equipe');
    case GoalTrigger.loyaltyGap: return __t('T2 · fidelização abaixo da média');
    default: return String(trigger);
  }
};

/** "Conservadora: 5 (80% dos dias)": o nível sugerido, o valor da meta e o quanto ela já teria sido batida. */
export const optionLine = (type: OfferTargetType, option: OfferLevelOption): string => {
  const value = type === AttendantGoalType.PERCENTAGE_LOYALTY ? `${option.value}%` : String(option.value);
  return `${goalLevelLabel(option.level)}: ${value} (${__t('%{pct}% dos dias', { pct: option.daysHitRatePct })})`;
};

export const exclusionLabel = (reason: StationExclusion): string => {
  switch (reason) {
    case StationExclusion.noData: return __t('poucos abastecimentos');
    case StationExclusion.smallTeam: return __t('equipe pequena');
    case StationExclusion.sharedCard: return __t('cartão compartilhado');
    case StationExclusion.lowCoverage: return __t('pouca tag de frentista');
    case StationExclusion.numericIdsMismatch: return __t('posto sem concentrador/ids divergentes');
    default: return String(reason);
  }
};

export const workerStatusLabel = (status: WorkerRunStatus): string =>
  (status === WorkerRunStatus.done ? __t('Concluída') : __t('Em andamento'));

// Datas das telas internas: pt-BR, sempre no horário de Brasília (o navegador pode estar em qualquer fuso).
export const DATE_TIME: Intl.DateTimeFormatOptions = {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
};

export const fmtDateTime = (value?: string | null): string => {
  if (!value) { return '—'; }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : new Intl.DateTimeFormat('pt-BR', DATE_TIME).format(d);
};

/** 'YYYY-MM-DD' (ou ISO) sem passar por Date, para não deslocar o dia por fuso. */
export const fmtDay = (value?: string | null): string => {
  if (!value) { return '—'; }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

export const fmtDuration = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) { return '—'; }
  const s = Math.round(Number(seconds));
  if (s < 60) { return `${s}s`; }
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}min ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}min`;
};

export const shortId = (id?: string | null): string => (id ? String(id).slice(0, 8) : '—');

/** Convite enviado: `n` postos com gatilho; se a rede já tinha convite, ele foi só recalculado. */
export const sentMessage = (result: SendOfferResult, chainName: string): string =>
  (result.outcome === SendOfferOutcome.refreshed
    ? __t('O convite da rede %{rede} já existia e foi recalculado.', { rede: chainName })
    : __t('Convite enviado para %{rede}: %{n} posto(s) com gatilho.', { rede: chainName, n: result.stationsWithTrigger }));

/** Recusa do envio: a frase e, quando há, o detalhe por motivo (uma linha cada). */
export interface SendRefusal { message: string; details?: string[] }

const reasonLine = (reason: StationExclusion, count: unknown): string | null =>
  (Number(count) > 0 ? `${exclusionLabel(reason)}: ${Number(count)}` : null);

/** Código desconhecido, 5xx ou erro de rede caem na frase genérica. */
export const sendRefusal = (error: SendOfferError): SendRefusal => {
  switch (error.code) {
    case SendOfferErrorCode.chainNotFound: return { message: __t('Rede não encontrada.') };
    case SendOfferErrorCode.moduleActive:
      return { message: __t('A rede já usa a Meta de Frentista (contratada ou em degustação).') };
    case SendOfferErrorCode.hasBoards: return { message: __t('A rede já tem quadros de meta.') };
    case SendOfferErrorCode.trialAlreadyUsed: return { message: __t('A rede já fez a degustação.') };
    case SendOfferErrorCode.dismissedRecently:
      return {
        message: __t('O dono da rede clicou em "Agora não" em %{data}. A rede fica 60 dias sem convite.', {
          data: fmtDateTime(error.data?.dismissedAt),
        }),
      };
    case SendOfferErrorCode.notEligible: return { message: __t('A rede não está elegível para o convite agora.') };
    case SendOfferErrorCode.noStation: {
      const mismatched = error.data?.mismatched;
      const line = reasonLine(StationExclusion.numericIdsMismatch, Array.isArray(mismatched) ? mismatched.length : mismatched);
      return { message: __t('Nenhum posto da rede tem concentrador para a análise.'), details: line ? [line] : [] };
    }
    case SendOfferErrorCode.noStationAnalyzable: {
      const excluded = error.data?.excluded ?? {};
      return {
        message: __t('Nenhum posto da rede tem dados suficientes para a análise.'),
        details: Object.values(StationExclusion)
          .map(reason => reasonLine(reason, excluded[reason]))
          .filter((line): line is string => line !== null),
      };
    }
    case SendOfferErrorCode.noTrigger:
      return { message: __t('Nenhum posto da rede tem gatilho: os frentistas estão parecidos entre si.') };
    case SendOfferErrorCode.sendInProgress: return { message: __t('Já existe um envio em andamento. Tente de novo em instantes.') };
    case SendOfferErrorCode.analysisUnavailable:
      return { message: __t('A análise não respondeu agora. Tente de novo em alguns minutos.') };
    default: return { message: __t('Não foi possível enviar o convite agora. Tente de novo.') };
  }
};
